import { Box } from '@mui/material';
import { useMemo } from 'react';

import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { TwoLineTextRenderer } from '@/components/tables/DataTable/renderers/cell/TwoLineTextRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { DOCUMENT_TYPE_DISPLAY_NAMES } from '@/modules/documents/documents.constants';
import {
  DocumentSummaryPromptDocumentType,
  DocumentType,
} from '@/types/schema';
import { formatEnumCase } from '@/utils/formatting/strings';

import { AdminSummaryPromptsPage_DocumentSummaryPromptFragment } from './graphql/AdminSummaryPromptsPage.generated';

export interface Row {
  id: string;
  name: {
    lineOne: string;
  };
  documentTypeDisplay: string;
  shouldAutoRun: boolean;
  isDefaultForDocType: boolean;
  createdBy: string;
}

function getDocumentTypeDisplayFromPrompt(
  docType: DocumentSummaryPromptDocumentType
): string {
  try {
    return (
      DOCUMENT_TYPE_DISPLAY_NAMES[docType as unknown as DocumentType] ??
      formatEnumCase(docType)
    );
  } catch {
    return formatEnumCase(docType);
  }
}

export function mapPromptsToRows(
  prompts: AdminSummaryPromptsPage_DocumentSummaryPromptFragment[]
): Row[] {
  return prompts
    .slice()
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
    .map((prompt) => {
      const documentTypeDisplay = getDocumentTypeDisplayFromPrompt(
        prompt.documentType
      );
      return {
        id: prompt.id,
        name: {
          lineOne: prompt.displayName,
        },
        documentTypeDisplay,
        shouldAutoRun: prompt.shouldAutoRun,
        isDefaultForDocType: prompt.isDefaultForDocType,
        createdBy:
          `${prompt.createdByUser.firstName} ${prompt.createdByUser.lastName}`.trim(),
      };
    });
}

export function useSummaryPromptColumns(): Column<Row>[] {
  return useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        renderCell: TwoLineTextRenderer({
          lineOne: ({ row }) => row.name.lineOne,
          lineOneProps: { variant: 'title-xs' },
        }),
      },
      {
        field: 'documentTypeDisplay',
        headerName: 'Document type',
        flex: 1,
        renderCell: TextRenderer({
          text: ({ row }) => row.documentTypeDisplay,
        }),
      },
      {
        field: 'createdBy',
        headerName: 'Created by',
        flex: 1,
        minWidth: 160,
        renderCell: TextRenderer(),
      },
      {
        field: 'shouldAutoRun',
        align: 'center',
        sortable: false,
        width: 200,
        minWidth: 200,
        maxWidth: 200,
        renderCell: TextRenderer({
          text: ({ row }) => {
            if (!row.shouldAutoRun && !row.isDefaultForDocType) {
              return null;
            }
            return (
              <Box m={1} display="flex" gap={0.5}>
                {row.shouldAutoRun && (
                  <Tooltip
                    title={`This prompt auto-runs for "${row.documentTypeDisplay}" document uploads.`}
                  >
                    <Badge variant={BadgeVariants.Accent} display="Auto run" />
                  </Tooltip>
                )}
                {row.isDefaultForDocType && (
                  <Tooltip title="When enabled, the summary will appear first and be pre-selected for new summaries">
                    <Badge
                      variant={BadgeVariants.SecondaryOutline}
                      display="Default"
                    />
                  </Tooltip>
                )}
              </Box>
            );
          },
        }),
      },
    ],
    []
  );
}
